import { SvgIcon } from "@progress/kendo-react-common";
import { Window, WindowActionsBar } from "@progress/kendo-react-dialogs"
import { Editor, EditorChangeEvent, EditorTools } from "@progress/kendo-react-editor";
import * as svg from '@progress/kendo-svg-icons'
import { MessageApi } from "../../../redux/api/MessageApi";
import { useEffect, useState } from "react";
import { expiryType, IMessageConfig } from "../../../types/User/IMessageConfig";
import { DateRangePicker, DateTimePicker } from '@progress/kendo-react-dateinputs';
import { MultiSelect } from '@progress/kendo-react-dropdowns';
import { UserApi } from "../../../redux/api/UserApi";
import { ITeam } from "../../../types/ITeam";
import { PanelBar, PanelBarItem } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import JsonTreeViewer from "../../Test/JsonTreeViewer";
import { ClientApi } from "../../../redux/api/ClientApi";
import { IClientType } from "../../../types/Client/IClientType";
import { IClient } from "../../../types/Client/IClient";
import { IAudience } from "../../../types/User/IAudience";
import { IOffice } from "../../../types/Client/IOffice";
import { editorStyles } from "../../Common/TextEditor";
import { IMonitor } from "../../../types/User/IMonitor";

const {
    Bold,
    Undo,
    Redo,
    Link,
    Unlink,
} = EditorTools;

const closeBtn = (fn: () => void) => (
    <SvgIcon className="close-button" icon={svg.xIcon} onClick={() => fn()} />
)

export const EditWindow = ({ close, config }: { close: () => void, config: IMessageConfig }) => {
    const { data: profile } = UserApi.useGetUserProfileQuery()
    const { data: monitorData } = UserApi.useGetUserMonitorQuery()
    const { data: teamList } = UserApi.useGetTeamsQuery();
    const { data: clientTypeData } = ClientApi.useGetClientTypesQuery();
    const { data: clientData } = ClientApi.useGetMyClientsQuery();
    const allTeams = teamList ?? []
    const [textValue, setText] = useState('')
    const [important, setImportant] = useState(false)
    const [monitor, setMonitor] = useState<IMonitor[]>(config?.Default_Security?.filter(v => v.Security_Type_Enum === 2).map(v => {
        return { Id: v.Lookup_Id, Display: v.Display_Name } as IMonitor
    }))
    const [teams, setTeams] = useState<ITeam[]>(config?.Default_Security?.filter(v => v.Security_Type_Enum === 1).map(v => {
        return { ID: v.Lookup_Id === (-1) ? profile?.Team_Id : v.Lookup_Id, Dept_Name: v.Lookup_Id === (-1) ? `(Your ${profile.Phrases.Team})` : v.Display_Name } as ITeam
    }))
    const [clients, setClients] = useState<IClient[]>(config?.Default_Security?.filter(v => v.Security_Type_Enum === 3).map(v => {
        return { ID: v.Lookup_Id, Company_Name: v.Display_Name } as IClient
    }))
    const [clientTypes, setClientTypes] = useState<IClientType[]>(config?.Default_Security?.filter(v => v.Security_Type_Enum === 4).map(v => {
        return { ID: v.Lookup_Id, Company_Type_Name: v.Display_Name } as IClientType
    }))
    const [offices, setOffices] = useState<IOffice[]>(config?.Default_Security?.filter(v => v.Security_Type_Enum === 7).map(v => {
        return { ID: v.Lookup_Id, Address_Name: v.Display_Name } as IOffice
    }))
    const { data: officeData } = ClientApi.useGetClientOfficesQuery(clients && clients[0]?.ID, { skip: clients ? clients.length !== 1 : false })
    const [createMessageEndpoint] = MessageApi.useCreateMessageMutation()
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [expiryDate, setexpiryDate] = useState<Date | null>(null);

    useEffect(() => {
        if (config) {
            const now = new Date()
            var extratime: number
            switch (config.Default_Expire_Type) {
                case expiryType.Minutes: extratime = 60000; break;
                case expiryType.Hours: extratime = 3.6e6; break;
                case expiryType.Days: extratime = 8.64e7; break;
                case expiryType.Months: extratime = 2.628e9; break;
                case expiryType.Years: extratime = 3.154e10; break;
            }
            extratime *= config.Default_Expire_Value

            setexpiryDate(new Date(now.getTime() + extratime));
            setStartDate(new Date());
        }
    }, [config]);

    function createNewMessage(content: string, isImportant: boolean): boolean {
        if (content === "") return false

        var exp: Date;


        var visibilityArr: IAudience[] = [];
        teams && visibilityArr.push(...teams?.map(v => ({ Lookup_Id: v.ID, Security_Type_Enum: 1 } as IAudience))) //teams
        clients && visibilityArr.push(...clients?.map(v => ({ Lookup_Id: v.ID, Security_Type_Enum: 3 } as IAudience))) //Client
        clientTypes && visibilityArr.push(...clientTypes?.map(v => ({ Lookup_Id: v.ID, Security_Type_Enum: 4 } as IAudience))) //Client type
        monitor && visibilityArr.push(...monitor?.map(v => ({ Lookup_Id: v.Group_Id, Security_Type_Enum: 2 } as IAudience))) //Call monitor


        createMessageEndpoint({
            expires: expiryDate,
            important: isImportant,
            text: content,
            startdt: startDate,
            visibility: visibilityArr
        })

        return true
    }

    const updateTeams = (event) => {
        setTeams([...event.value]);
    };

    const updateClients = (event) => {
        setClients([...event.value]);
    };
    const updateClientTypes = (event) => {
        setClientTypes([...event.value]);
    };

    var teamListMap = {}
    allTeams.forEach(v => {
        teamListMap[v.Dept_Name] = v
    })



    const onMount = (event) => {
        const iframeDocument = event.dom.ownerDocument;
        const style = iframeDocument.createElement("style");
        style.appendChild(iframeDocument.createTextNode(editorStyles));
        iframeDocument.head.appendChild(style);
    }

    return (
        <Window
            className="editor-window cool-window"
            modal={true}
            resizable={false}
            initialHeight={600}
            initialWidth={Math.min(900, window.innerWidth - 10)}
            minimizeButton={() => null}
            //closeButton={() => closeBtn(close)}
            onClose={() => close()}
            title={<div className="d-flex"><SvgIcon icon={svg.bellIcon} className="m-1" /> Send notification</div>}
        >

            <PanelBar style={{ marginRight: '8px' }}>
                <PanelBarItem expanded={true} title="Message">
                    <div className="p-2">
                        <Editor
                            tools={[
                                [Bold],
                                [Undo, Redo],
                                [Link, Unlink],
                            ]}
                            contentStyle={{ height: 630 }}
                            onChange={(event: EditorChangeEvent) => setText(event.html)}
                            value={textValue}
                            onMount={onMount}
                        /></div>
                </PanelBarItem>
                <PanelBarItem expanded={true} title="Visibility settings">
                    <div className='flex flex-wrap p-2'>
                        <div className='k-form-field col-12 col-lg-6 p-2'>
                            <label>{profile.Phrases.Team}</label>
                            <div>
                                <MultiSelect popupSettings={{ style: { zIndex: 10030, width: '400px' } }}
                                    data={allTeams}
                                    onChange={updateTeams}
                                    textField="Dept_Name"
                                    value={teams}
                                    placeholder="Select..."
                                    style={{ width: 400 }}
                                />
                            </div>
                        </div>
                        <div className='k-form-field col-12 col-lg-6 p-2'>
                            <label>{profile.Phrases.CallPhrase} Monitor</label>
                            <div>
                                <MultiSelect popupSettings={{ style: { zIndex: 10030, width: '400px' } }}
                                    data={monitorData?.filter(m => m.Type_Id === 1 || m.Type_Id === 2) ?? []}
                                    onChange={(event) => setMonitor([...event.value])}
                                    textField="Display"
                                    value={monitor}
                                    placeholder="Select..."
                                    style={{ width: 400 }}
                                />
                            </div>
                        </div>
                        {config?.Create_SSI_Message && <><div className='k-form-field col-12 col-lg-6 p-2'>
                            <label>{profile.Phrases.Client} Types</label>
                            <div>
                                <MultiSelect popupSettings={{ style: { zIndex: 10030, width: '400px' } }}
                                    data={clientTypeData}
                                    onChange={updateClientTypes}
                                    textField="Company_Type_Name"
                                    value={clientTypes}
                                    placeholder="Select..."
                                    style={{ width: 400 }}
                                />
                            </div>
                        </div>
                            <div className='k-form-field col-12 col-lg-6 p-2'>
                                <label>{profile.Phrases.Client} / {profile.Phrases.Office}</label>
                                <div>
                                    <MultiSelect popupSettings={{ style: { zIndex: 10030, width: '400px' } }}
                                        data={clientData}
                                        onChange={updateClients}
                                        textField="Company_Name"
                                        value={clients}
                                        placeholder="Select..."
                                        style={{ width: 400 }}
                                    />
                                    <MultiSelect popupSettings={{ style: { zIndex: 10030, width: '400px' } }}
                                        data={officeData}
                                        onChange={(e => setOffices([...e.value]))}
                                        textField="Address_Name"
                                        value={offices}
                                        placeholder="Select..."
                                        style={{ width: 400 }}
                                    />
                                </div>
                            </div></>}
                    </div>

                    <div className="dates flex flex-wrap">
                        <div className="start-date k-form-field  col-12 col-lg-6 p-2">
                            <label>Start</label>
                            <div><DateTimePicker
                                placeholder="Start date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.value)}
                                format={"yyyy-dd-MM HH:mm"}
                                width={200}
                            /></div>

                        </div>
                        <div className="end-date k-form-field  col-12 col-lg-6 p-2">
                            <label>End</label>
                            <div><DateTimePicker
                                placeholder="Expiry date"
                                value={expiryDate}
                                onChange={(e) => setexpiryDate(e.value)}
                                format={"yyyy-dd-MM HH:mm"}
                                width={200}

                            /></div>
                        </div>
                    </div>

                </PanelBarItem>
                {/* <PanelBarItem expanded={true} title="Debug">
                    <JsonTreeViewer data={config} title="config" >
                    </JsonTreeViewer>
                </PanelBarItem> */}
            </PanelBar>

            <WindowActionsBar>
                <div className="button-panel">
                    <div>
                    </div>
                    <Button svgIcon={important ? svg.checkIcon : svg.exclamationCircleIcon} className={`important-btn ${!important ? "is-important" : ""}`} onClick={() => setImportant(v => !v)}>
                        Important
                    </Button>
                    <Button svgIcon={svg.paperPlaneIcon} type='button' className='send-btn' style={{ color: '#fff', backgroundColor: profile?.Nav_Hex_Color }}
                        onClick={() => {
                            if (createNewMessage(textValue, important))
                                close()
                        }}>
                        Send
                    </Button>
                    {/* Close button */}
                    <Button fillMode='outline' svgIcon={svg.cancelIcon} onClick={(e) => close()} className='cancel-btn'>
                        Cancel     </Button>
                </div>
            </WindowActionsBar>
        </Window>
    )
}

